import { useEffect, useRef, useState } from "react";
import { Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import SendIcon from "@mui/icons-material/Send";
import { ChatMessage } from "../../hooks/useCoachChat";

interface ChatPaneProps {
  messages: ChatMessage[];
  streamingText: string;
  sending: boolean;
  onSend: (text: string) => void;
}

function Bubble({ role, content }: { role: string; content: string }) {
  const isUser = role === "user";
  return (
    <Stack direction="row" justifyContent={isUser ? "flex-end" : "flex-start"}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: "80%",
          px: 2,
          py: 1.25,
          borderRadius: 2.5,
          bgcolor: isUser ? "primary.main" : "background.default",
          color: isUser ? "primary.contrastText" : "text.primary",
          border: isUser ? 0 : 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
          {content}
        </Typography>
      </Paper>
    </Stack>
  );
}

export default function ChatPane({ messages, streamingText, sending, onSend }: ChatPaneProps) {
  const { t } = useTranslation("coach");
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, streamingText]);

  const submit = () => {
    const text = draft.trim();
    if (!text || sending) return;
    onSend(text);
    setDraft("");
  };

  return (
    <Stack sx={{ height: "100%" }} spacing={2}>
      <Stack
        ref={listRef}
        role="log"
        aria-live="polite"
        spacing={1.5}
        sx={{ flex: 1, overflowY: "auto", pr: 1 }}
      >
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} content={m.content} />
        ))}
        {sending && streamingText && <Bubble role="assistant" content={streamingText} />}
      </Stack>
      <Box>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={5}
            placeholder={t("ChatPane.messagePlaceholder")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            disabled={sending}
          />
          <Button
            variant="contained"
            color="secondary"
            endIcon={<SendIcon />}
            onClick={submit}
            disabled={sending || !draft.trim()}
            sx={{ fontWeight: 500, px: 3, py: 1.4 }}
          >
            {t("ChatPane.sendButton")}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
